"use client";

import { usePathname } from "next/navigation";

import MarketingNav from "~/app/components/marketing-nav";
import MarketingFooter from "~/app/components/marketing-footer";
import { scannerHomeHref } from "~/lib/siteglue/agent-ready-host";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";

type InboundGptMarketingShellProps = {
  children: React.ReactNode;
  scannerHost?: boolean;
  brandId?: "siteglue" | "inbound_gpt";
};

function ScannerHeader({ homeHref }: { homeHref: string }) {
  return (
    <header className="border-b border-stone-200 bg-white">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
        <a href={homeHref} className="flex min-w-0 items-center gap-2">
          <img src="/siteglue/logo.png" alt="SiteGlue" className="h-8 w-auto" />
          <span className="truncate text-sm font-semibold text-slate-900">Agent Readiness</span>
        </a>
        <a
          href="https://www.siteglue.ai"
          target="_blank"
          rel="noreferrer"
          className="shrink-0 text-sm font-semibold text-[#4f81bd]"
        >
          Learn more about SiteGlue AI
        </a>
      </div>
    </header>
  );
}

/** Shared chrome for all marketing routes at site root. */
export function InboundGptMarketingShell({
  children,
  scannerHost = false,
  brandId,
}: InboundGptMarketingShellProps) {
  const pathname = usePathname();
  if (scannerHost || pathname.startsWith("/agent-ready/")) {
    return (
      <div className="min-h-screen bg-[#f7f6f3] text-slate-900">
        <ScannerHeader homeHref={scannerHomeHref(scannerHost)} />
        <main>{children}</main>
      </div>
    );
  }

  // Client hostname → correct twin chrome (siteglue.ai vs easygpt.builders).
  const resolvedBrandId = brandId ?? getMarketingChrome().brandId;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav brandId={resolvedBrandId} />
      <main>{children}</main>
      <MarketingFooter isLanding={false} brandId={resolvedBrandId} />
    </div>
  );
}
